/**
 * Account menu (APP-001 §14, APP-009 §16).
 *
 * Renders safe account metadata from the backend bootstrap response (never
 * Clerk profile data as authority). The displayed name comes from the backend
 * account; email is shown only when Clerk exposes it (display-only, not
 * authority). The menu groups quick navigation (Account, Billing, API docs,
 * Support), a theme toggle, and sign-out (which clears the Clerk session).
 *
 * Keyboard accessibility: Radix DropdownMenu restores focus to the trigger on
 * close and is fully operable by keyboard (arrow keys, Escape, Enter/Space).
 */
import { Link } from 'react-router-dom';
import { BookOpen, CreditCard, HelpCircle, LogOut, Moon, Sun, User } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useSignOutAndRedirect } from '@/auth/useSignInHelpers';
import { getAppLinks } from '@/config/links';
import { track } from '@/lib/analytics';
import type { DashboardIdentityResponse } from '@/api/dashboard-types';
import { useUser } from '@clerk/clerk-react';

export function AccountMenu({ identity }: { identity: DashboardIdentityResponse }) {
  const { theme, setTheme } = useTheme();
  const signOut = useSignOutAndRedirect();
  const { user, isLoaded } = useUser();
  const links = getAppLinks();
  // Display-only Clerk email; the backend account is the authority for
  // account/plan/quota. Never shown in analytics. Full resolution chain:
  // primaryEmailAddress can be null while emailAddresses exist (and while
  // !isLoaded), so resolve through the id-referenced address, then the first
  // known address, before omitting the line.
  const email =
    user?.primaryEmailAddress?.emailAddress
    ?? user?.emailAddresses?.find((e) => e.id === user?.primaryEmailAddressId)?.emailAddress
    ?? user?.emailAddresses?.[0]?.emailAddress
    ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
            <User className="h-4 w-4" />
          </span>
          <span className="hidden max-w-[140px] truncate text-sm font-medium sm:inline">
            {identity.account.account_name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="truncate text-sm font-medium">{identity.account.account_name}</span>
            {!isLoaded ? (
              <span className="truncate text-xs font-normal text-muted-foreground">Loading…</span>
            ) : email ? (
              <span className="truncate text-xs font-normal text-muted-foreground">{email}</span>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account">
            <User className="h-4 w-4" />
            <span>Account</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/billing">
            <CreditCard className="h-4 w-4" />
            <span>Billing</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={links.apiDocs}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track({ event: 'account_docs_opened' })}
          >
            <BookOpen className="h-4 w-4" />
            <span>API documentation</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={links.support}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track({ event: 'account_support_opened' })}
          >
            <HelpCircle className="h-4 w-4" />
            <span>Support</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          <span>{theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            track({ event: 'account_sign_out_started' });
            void signOut();
          }}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
